import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuBriefcase,
    LuGlobe,
    LuClock,
    LuShield,
    LuChevronRight
} from 'react-icons/lu';

const WebDevelopment = () => {
    const [hoveredRole, setHoveredRole] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const titleParts = "Website Development".split(' ');
    const firstWord = titleParts[0];
    const restOfTitle = titleParts.slice(1).join(' ');

    return (
        <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-primary py-20 lg:py-32">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center space-x-2 text-blue-100 font-bold tracking-wider text-sm mb-6 uppercase">
                        <span>Our Services</span>
                    </div>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
                        {firstWord} <span className="text-white">{restOfTitle}</span>
                    </h1>
                    <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Build scalable, secure, and high-performance web applications tailored to your business needs.
                    </p>
                </div>
            </section>

            {/* Specialized Solutions Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Core Capabilities</h2>
                        <p className="text-xl text-slate-600">
                            Discover our tailored solutions mapped to your business objectives.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: LuBriefcase,
                                title: "Custom Enterprise Web Apps",
                                desc: "Tailor-made web solutions designed to optimize your internal corporate workflows."
                            },
                            {
                                icon: LuBriefcase,
                                title: "Progressive Web Applications",
                                desc: "PWA delivery with offline support and lightning-fast load times."
                            },
                            {
                                icon: LuBriefcase,
                                title: "API Integration & Development",
                                desc: "Seamless integration points across various external services."
                            }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="relative overflow-hidden bg-white rounded-[2rem] p-10 border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group text-center flex flex-col items-center"
                                onMouseEnter={() => setHoveredRole(index)}
                                onMouseLeave={() => setHoveredRole(null)}
                                style={{
                                    backgroundImage: hoveredRole === index
                                        ? `linear-gradient(to bottom right, #2563eb0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className="relative z-10 w-full flex flex-col items-center">
                                    <div className="w-16 h-16 bg-blue-100/50 rounded-full flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform duration-300"
                                        style={{ boxShadow: `0 10px 15px -3px #2563eb33` }}>
                                        {React.createElement(item.icon as any, { className: "w-7 h-7" })}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3 transition-colors duration-300"
                                        style={{ color: hoveredRole === index ? '#2563eb' : undefined }}>
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed text-sm">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Optimization Section */}
            <section className="py-24 bg-slate-50 border-t border-slate-200">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>Global Delivery</span>
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900">Optimized for Excellence</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12 text-center">
                        {[
                            {
                                icon: LuGlobe,
                                title: "Global Standards",
                                desc: "Following international best practices for robust delivery."
                            },
                            {
                                icon: LuClock,
                                title: "Agile Development",
                                desc: "Iterative sprints tailored to meet market deadlines quickly."
                            },
                            {
                                icon: LuShield,
                                title: "Secure by Design",
                                desc: "Compliant with strict data protection regulations globally."
                            }
                        ].map((item, index) => (
                            <div key={index} className="flex flex-col items-center">
                                <div className="w-20 h-20 bg-white border border-slate-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                                    {React.createElement(item.icon as any, { className: "w-8 h-8 text-blue-600" })}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600 max-w-xs mx-auto text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Ready to Start Your Project?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Partner with us to transform your business operations and drive digital success.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-bold shadow-lg gap-2"
                        >
                            Contact Us Today
                            {React.createElement(LuChevronRight as any, { className: "w-5 h-5" })}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WebDevelopment;
